import React, { useState, useEffect, useMemo, useImperativeHandle, forwardRef } from "react";
import { CardBody, Row, Col, FormGroup, Label, Input, Button,Card } from "reactstrap";
import Select from "components/Select";
import { useGetChannels } from "api/channels/service";
import { getActiveColorStyle } from "../../utils/colorUtils";
import { getSelectStyles } from "../../utils/selectStyles";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";
import { formatPercentageInput, displayPercentage, toNumber } from "../../utils/currency";

const PortfolioInvestors = forwardRef(({ data, activeColor, isTheme2 = false, portfolioId, updateMutation }, ref) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const [isLightTheme, setIsLightTheme] = useState(false);
  const { data: investorsData } = useGetChannels("investor");
  
  const [investors, setInvestors] = useState([]);
  const [focusedField, setFocusedField] = useState(null);

  useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const selectStyles = useMemo(
    () =>
      isTheme2
        ? getTheme2SearchFilterSingleSelectStyles(activeColorStyle, true)
        : getSelectStyles(activeColorStyle, isLightTheme),
    [activeColorStyle, isLightTheme, isTheme2]
  );
  const inputStyle = isTheme2
    ? { textAlign: "right", fontSize: "14px" }
    : { textAlign: "right" };

  useEffect(() => {
    if (data && data.length > 0) {
      setInvestors(data.map(inv => ({
        investor: inv.investor,
        ownership: inv.ownership ? String(inv.ownership) : "0"
      })));
    } else {
      // Start with one empty row
      setInvestors([{ investor: "", ownership: "0" }]);
    }
  }, [data]);

  const handleInvestorChange = (index, investorId) => {
    const newInvestors = [...investors];
    newInvestors[index] = { ...newInvestors[index], investor: investorId || "" };
    setInvestors(newInvestors);
  };

  const handleOwnershipChange = (index, value) => {
    const formatted = formatPercentageInput(value);
    const newInvestors = [...investors];
    newInvestors[index] = { ...newInvestors[index], ownership: formatted };
    setInvestors(newInvestors);
  };

  const handleAddInvestor = () => {
    setInvestors([...investors, { investor: "", ownership: "0" }]);
  };

  const handleRemoveInvestor = (index) => {
    if (investors.length > 1) {
      const newInvestors = investors.filter((_, i) => i !== index);
      setInvestors(newInvestors);
    }
  };

  // Expose handleSave to parent via ref
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      try {
        // Filter out empty investors
        const validInvestors = investors
          .filter(inv => inv.investor)
          .map(inv => ({
            investor: inv.investor,
            ownership: toNumber(inv.ownership)
          }));

        const payload = {
          loan_investor: validInvestors
        };

        await updateMutation.mutateAsync({
          id: portfolioId,
          data: payload
        });

        return { success: true };
      } catch (error) {
        console.error("Error saving investors:", error);
        return { success: false, error };
      }
    }
  }));

  return (
    <Card>
      <CardBody>

      {investors.map((investor, index) => (
        <Row key={index} className=" align-items-end">
          <Col md="6">
            <FormGroup>
              <Label style={{ fontSize: 12 }}>Investor</Label>
              <Select
                activeColor={activeColor}
                styles={selectStyles}
                className="react-select"
                placeholder="Select Investor"
                name={`investor_${index}`}
                isSearchable
                closeMenuOnSelect={true}
                value={investor.investor ? { 
                  value: investor.investor, 
                  label: investorsData?.results?.find((i) => i.id === investor.investor)?.name || investor.investor 
                } : null}
                onChange={(value) => handleInvestorChange(index, value?.value)}
                options={investorsData?.results?.map((i) => ({ value: i.id, label: i.name }))}
                isClearable
              />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label style={{ fontSize: 12 }}>% Ownership</Label>
              <Input
                type="text"
                className="form-control"
                value={displayPercentage(investor.ownership, focusedField === `ownership_${index}`)}
                onChange={(e) => handleOwnershipChange(index, e.target.value)}
                onFocus={() => setFocusedField(`ownership_${index}`)}
                onBlur={() => setFocusedField(null)}
                style={inputStyle}
                placeholder="0%"
              />
            </FormGroup>
          </Col>
        </Row>
      ))}
        <Button
          size="sm"
          style={{ ...activeColorStyle, color: 'white' }}
          onClick={handleAddInvestor}
        >
          <i className="tim-icons icon-simple-add" style={{ marginRight: '5px' }} />
          Add New
        </Button>
      </CardBody>
    </Card>
  );
});

PortfolioInvestors.displayName = "PortfolioInvestors";

export default PortfolioInvestors;

